import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import HeroBanner from '../components/HeroBanner';
import {
  IoIosMenu, IoIosCart, IoIosHeart, IoIosArrowDown,
  IoIosRemove, IoIosAdd, IoIosStar, IoIosStarOutline
} from 'react-icons/io';
import { FiPhone, FiSend } from 'react-icons/fi';
import { MdShoppingCart } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { FaBars } from 'react-icons/fa';
import { useParams } from "react-router-dom";
import { AiOutlineUser } from 'react-icons/ai';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios, { AxiosError } from 'axios';
import { apiClient } from '../config/apiConfig';

interface Variant {
  variantId: number;
  weight: number;
  unit: "kg" | "gms";
  price: number;
  stock: number;
}

interface Product {
  productId: number;
  productName: string;
  productPrice: number;
  productQuantity: number;
  productDescription: string;
  productImage: string;
  variants: Variant[];
}
interface CartItem {
  cartItemsId: number;
  productId: number;
  variantId: number | null;
  productName: string;
  variantDescription: string | null;
  priceAtPurchase: number;
  quantity: number;
  productImage: string;
}

const ProductSingle: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [rating, setRating] = useState<number>(0);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [cartId, setCartId] = useState<number | null>(
    localStorage.getItem("cartId") ? Number(localStorage.getItem("cartId")) : null
  );

  // Load customer name from localStorage
  useEffect(() => {
    const name = localStorage.getItem("customerName");
    if (name && name.trim() !== "" && name !== "undefined") {
      setCustomerName(name);
    } else {
      setCustomerName(null);
    }
  }, []);

  // Load external script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/js/main.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Fetch product by ID
  useEffect(() => {
    if (!productId) return;

    const fetchProduct = async () => {
      try {
        const res = await axios.get(`https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/AddProduct/GetProductById?id=${productId}`);
        setSelectedProduct(res.data);

        if (res.data.variants && res.data.variants.length > 0) {
          setSelectedVariant(res.data.variants[0]);
          setQuantity(1);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
      }
    };

    fetchProduct();
  }, [productId]);

  // Variant change handler
  const handleVariantChange = (variantId: number) => {
    if (!selectedProduct) return;
    const variant = selectedProduct.variants.find(v => v.variantId === variantId);
    if (variant) {
      setSelectedVariant(variant);
      setQuantity(1);
    }
  };

  // Increment / Decrement
  const handleIncrement = () => {
    if (!selectedVariant) return;
    setQuantity(prev => Math.min(prev + 1, selectedVariant.stock));
  };

  const handleDecrement = () => {
    setQuantity(prev => Math.max(prev - 1, 1));
  };

  // Editable input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedVariant) return;

    const val = e.target.value.replace(/\D/g, ""); // keep only digits

    if (val === "") {
      // allow user to delete all and type a new number
      setQuantity(0);
      return;
    }

    let num = parseInt(val, 10);

    // Clamp to stock limit
    if (num > selectedVariant.stock) num = selectedVariant.stock;

    setQuantity(num);
  };

  // On leaving the input, correct the value if empty or invalid
  const handleInputBlur = () => {
    if (!quantity || quantity < 1) setQuantity(1);
  };

  // Rating handler
  const handleRating = (value: number) => setRating(value);

  // Add to cart
  const handleAddToCart = async (product: Product): Promise<boolean> => {
    try {
      const customerId = localStorage.getItem("customerId");
      if (!customerId) {
        toast.error("Please login first!");
        return false;
      }

      // Use selectedVariant from state
      const selectedVariantLocal = selectedVariant;

      const cartItemData = {
        customerId: Number(customerId),
        productId: product.productId,
        variantId: selectedVariantLocal?.variantId || null,
        priceAtPurchase:
          selectedVariantLocal?.price ?? product.productPrice, // ✅ locked price
        quantity: quantity || 1,
      };

      const response = await apiClient.post(
        "/Cart/CreateOrAddCartItem",
        cartItemData
      );

      if (response.status === 200) {
        const { cartId } = response.data;
        if (cartId) {
          setCartId(cartId);
          localStorage.setItem("cartId", cartId.toString());
        }

        // ✅ Refresh cart from backend to update cart count
        await fetchCart();

        toast.success(
          `${quantity} × ${product.productName} (${selectedVariantLocal ? `${selectedVariantLocal.weight}${selectedVariantLocal.unit}` : ''}) added to cart!`
        );
        return true;
      }
      return false;
    } catch (error: any) {
      console.error(
        "Error adding to cart:",
        error.response?.data || error.message
      );
      toast.error("Failed to add product to cart");
      return false;
    }
  };
  const fetchCart = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      if (!customerId) {
        console.error("No customerId found in localStorage");
        return;
      }

      const response = await apiClient.get(
        `/Cart/GetCartByCustomerId?customerId=${customerId}`
      );

      console.log("Cart data:", response.data);

      // ✅ directly set items (API already shaped as items[])
      setCartItems(response.data.items || []);

    } catch (error) {
      console.error("Error fetching cart items:", error);
    } finally {
      setLoading(false); // ✅ stop loading spinner
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const handleIncrease = async (item: CartItem) => {
    try {
      const updatedCart = cartItems.map((i) =>
        i.cartItemsId === item.cartItemsId
          ? { ...i, quantity: i.quantity + 1 }
          : i
      );
      setCartItems(updatedCart);

      // Optional: update quantity in backend
      await apiClient.put(`/Cart/UpdateQuantity`, {
        cartItemsId: item.cartItemsId,
        quantity: item.quantity + 1,
      });
    } catch (error) {
      console.error("Error increasing quantity:", error);
    }
  };

  const handleDecrease = async (item: CartItem) => {
    if (item.quantity <= 1) return; // prevent 0 quantity
    try {
      const updatedCart = cartItems.map((i) =>
        i.cartItemsId === item.cartItemsId
          ? { ...i, quantity: i.quantity - 1 }
          : i
      );
      setCartItems(updatedCart);

      // Optional: update quantity in backend
      await apiClient.put(`/Cart/UpdateQuantity`, {
        cartItemsId: item.cartItemsId,
        quantity: item.quantity - 1,
      });
    } catch (error) {
      console.error("Error decreasing quantity:", error);
    }
  };
  const totalCount = cartItems.length;
  return (
    <div className="goto-here">
      {/* Top Bar */}
      <div className="py-1 bg-primary w-100">
        <div className="container">
          <div className="row d-flex align-items-center text-white">
            <div className="col-12 col-md d-flex align-items-center mb-1 mb-md-0">
              <FiPhone size={14} className="mr-2" />
              <span>+91 9148761750</span>
            </div>

            <div className="col-12 col-md d-flex align-items-center mb-1 mb-md-0">
              <FiSend size={14} className="mr-2" />
              <span>care@fhome.shop</span>
            </div>

            <div className="col-12 col-md text-md-right">
              <span>3-5 Business days delivery & Free Returns</span>
            </div>
          </div>
        </div>
      </div>
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-dark ftco_navbar bg-dark ftco-navbar-light" id="ftco-navbar">
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" href="/">
            <img src="/images/fhome-logo.jpg" alt="" style={{ height: "55px", marginRight: "8px", borderRadius: "50%", width: "80px" }} />
            <span style={{ fontWeight: 'bold', fontSize: '20px', color: '#28a745' }}></span>
          </a>
          <button className="navbar-toggler" type="button" data-toggle="collapse" data-target="#ftco-nav">
            <FaBars className="mr-2" /> Menu
          </button>
          <div className="collapse navbar-collapse" id="ftco-nav">
            <ul className="navbar-nav ml-auto">
              <li className="nav-item"><a href="/" className="nav-link">Home</a></li>
              <li className="nav-item active dropdown">
                <a className="nav-link dropdown-toggle" href="#" data-toggle="dropdown">Shop</a>
                <div className="dropdown-menu">
                  <a className="dropdown-item" href="/shop">Shop</a>
                  <a className="dropdown-item" href="/wishlist">Wishlist</a>
                  <a className="dropdown-item" href="/product-single">Single Product</a>
                  <a className="dropdown-item" href="/cart">Cart</a>
                  <a className="dropdown-item" href="/checkout">Checkout</a>
                  <a className="dropdown-item" href="/my-orders">Orders</a>
                </div>
              </li>
              <li className="nav-item"><a href="/about" className="nav-link">About</a></li>
              {/* <li className="nav-item"><a href="/blog" className="nav-link">Blog</a></li> */}
              <li className="nav-item"><a href="/contact" className="nav-link">Contact</a></li>
              <li className="nav-item cta cta-colored"> <a href="/cart" className="nav-link">
                <span><MdShoppingCart size={18} /></span> [{totalCount}]
              </a></li>
              <li className="nav-item">
                <a
                  className="nav-link d-flex align-items-center"
                  href="/my-account"
                  style={{ cursor: "pointer", gap: "6px" }}
                >
                  <AiOutlineUser size={24} style={{ color: "#898989" }} />
                  {customerName ? (
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#999",
                        maxWidth: "120px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={`Hi, ${customerName}`}
                    >
                      Hi, {customerName}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#999"
                      }}
                    >
                      My Account
                    </span>
                  )}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <HeroBanner image="/images/fhome-oil-pour.jpg">
        <div className="row no-gutters slider-text align-items-center justify-content-center">
          <div className="col-md-9 ftco-animate text-center">
            <p className="breadcrumbs"><span className="mr-2"><a href="/">Home</a></span> <span>Product Single</span></p>
            <h1 className="mb-0 bread">Product Single</h1>
          </div>
        </div>
      </HeroBanner>

      {/* Product Detail Section */}
      {selectedProduct && selectedVariant && (
        <section className="ftco-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-6 mb-5">
                <div className="product-image-container">
                  <img
                    src={selectedProduct.productImage}
                    alt={selectedProduct.productName}
                    className="product-image"
                  />
                </div>
              </div>
              <div className="col-lg-6 product-details pl-md-5">
                <h3>{selectedProduct.productName}</h3>
                <p>{selectedProduct.productDescription}</p>

                {/* Variant */}
                <div className="mb-3">
                  <select className="form-control" value={selectedVariant.variantId} onChange={e => handleVariantChange(Number(e.target.value))}>
                    {selectedProduct.variants.map(v => (
                      <option key={v.variantId} value={v.variantId}>
                        {v.weight} {v.unit} - ₹{v.price}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price */}
                <p className="price">₹{selectedVariant.price.toFixed(2)}</p>

                {/* Quantity */}
                {selectedVariant && (
                  <div className="row mt-4">
                    <div className="input-group col-md-6 d-flex mb-3">
                      {/* Decrement */}
                      <button
                        type="button"
                        className="quantity-left-minus btn btn-secondary"
                        onClick={() => setQuantity((prev) => Math.max(prev - 1, 1))}
                        disabled={quantity <= 1}
                      >
                        <IoIosRemove size={20} />
                      </button>

                      {/* Editable Input */}
                      <input
                        type="text"
                        className="form-control text-center"
                        value={quantity === 0 ? "" : quantity} // show empty while typing
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                      />


                      {/* Increment */}
                      <button
                        type="button"
                        className="quantity-right-plus btn btn-secondary"
                        onClick={() =>
                          setQuantity((prev) => Math.min(prev + 1, selectedVariant.stock))
                        }
                        disabled={quantity >= selectedVariant.stock}
                      >
                        <IoIosAdd size={24} />
                      </button>
                    </div>
                  </div>
                )}


                <p> <a href="/cart" className="btn btn-black py-3 px-5" onClick={(e) => { e.preventDefault(); if (selectedProduct) handleAddToCart(selectedProduct); }}> Add to Cart </a> </p>
              </div>
            </div>
          </div>
        </section>
      )}
      {/* Related Products */}

      <div>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          pauseOnHover
          draggable
          theme="colored"
        />
      </div>
      {/* Newsletter */}
      <section className="ftco-section ftco-no-pt ftco-no-pb py-5 bg-light">
        <div className="container py-4">
          <div className="row d-flex justify-content-center py-5">
            <div className="col-md-6">
              <h2 style={{ fontSize: 22 }} className="mb-0">Subscribe to our Newsletter</h2>
              <span>Get e-mail updates about our latest shops and special offers</span>
            </div>
            <div className="col-md-6 d-flex align-items-center">
              <form action="#" className="subscribe-form">
                <div className="form-group d-flex">
                  <input type="text" className="form-control" placeholder="Enter email address" />
                  <input type="submit" value="Subscribe" className="submit px-3" />
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/919148761750"
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp size={40} color="#25D366" />
      </a>
    </div>
  );
};

export default ProductSingle;

